// src/data/wizardMockData.js
//
// In-memory mock of what's seeded in Mongo, shaped exactly like the
// real records described in architecture.md. The wizard pulls from
// this module today; swapping in a real `/api/agencies/:id/...`
// fetch layer later is a one-file change.
//
// What's modeled here:
//   - The A-Affordable agency (companyNumber 1008)
//   - Both of its documents from the Mongo seed (defendant
//     application + CCP)
//   - The subset of fields the wizard needs to render Step 4: the
//     `audience: agent` + `default_requirement: required` fields
//     referenced by the documents' bindings.
//
// Everything else from the 310-field collection (participant
// fields, photos, signatures, etc.) is intentionally absent — the
// wizard doesn't render those.
//
// Labels are en-US strings here for simplicity. The real Mongo
// records carry { 'en-US', 'es-MX', 'ru-RU' } maps; when the API
// is wired in, this module will pick the en-US value for the
// agent UI.

// ─── Sections ──────────────────────────────────────────────────
// Subset of the 17-section global ordering. Needed to group and
// sort the derived agent fields in Step 4.
const SECTIONS = {
  arrest_information: { order: 7,  label: 'Arrest information' },
  financial:          { order: 11, label: 'Financial' },
  payment:            { order: 12, label: 'Payment' },
};

// ─── Fields ─────────────────────────────────────────────────────
// Just the agent-audience required fields. Field shapes match
// the Mongo `fields` collection (architecture §5).
const FIELDS = {
  court_name: {
    field_id: 'court_name',
    type: 'text',
    audience: 'agent',
    section: 'arrest_information',
    section_order: 1,
    default_requirement: 'required',
    display_label: 'Court name',
    placeholder: 'Superior Court of King County',
  },
  case_number: {
    field_id: 'case_number',
    type: 'text',
    audience: 'agent',
    section: 'arrest_information',
    section_order: 2,
    default_requirement: 'required',
    display_label: 'Court case number',
    placeholder: '26-1-12345-6',
  },
  charges: {
    field_id: 'charges',
    type: 'text_multiline',
    audience: 'agent',
    section: 'arrest_information',
    section_order: 3,
    default_requirement: 'required',
    display_label: 'Charges',
    placeholder: 'DUI; reckless driving',
  },
  bond_amount_numeric: {
    field_id: 'bond_amount_numeric',
    type: 'currency',
    audience: 'agent',
    section: 'financial',
    section_order: 1,
    default_requirement: 'required',
    display_label: 'Bond amount',
    placeholder: '5,000',
  },
  premium_amount: {
    field_id: 'premium_amount',
    type: 'currency',
    audience: 'agent',
    section: 'financial',
    section_order: 2,
    default_requirement: 'required',
    display_label: 'Premium amount',
    placeholder: '500',
  },
  poa_number: {
    field_id: 'poa_number',
    type: 'text',
    audience: 'agent',
    section: 'financial',
    section_order: 3,
    // Held back from the wizard intentionally. POA allocation is
    // an unresolved open question (architecture doc §17, open Q5)
    // — agent-entered per case or auto-allocated from a surety
    // pool. Either way it's not asked for at intake; the agent
    // fills it in later (or the system auto-fills it) before the
    // document is generated. Set to 'required' if/when that
    // decision lands on "agent enters at intake."
    default_requirement: 'requested',
    display_label: 'POA number',
    placeholder: 'AS-1234567',
    why_we_need_this: 'Power of Attorney number from the surety',
  },
};

// ─── Documents ──────────────────────────────────────────────────
// The two A-Affordable documents from the Mongo seed.
//
// `bindings` here only shows the agent-audience entries — these
// are the bindings the wizard cares about for deriving Step 4. In
// the real Mongo records each document has dozens more bindings
// covering participant fields, but those don't affect what the
// wizard renders.
const DOCUMENTS = [
  {
    agency_id: 'a_affordable',
    companyNumber: 1008,
    document_slug: 'a_affordable_defendant_application',
    template_version: 1,
    display_name: 'Defendant Application',
    summary:
      "Captures the defendant's personal information, employment, " +
      'financial details, vehicle, references, and family contacts. ' +
      'Posted to the court as part of the bail filing.',
    signer_role: 'defendant',
    surety_affiliation: 'allegheny',
    status: 'active',
    bindings: {
      // (Only agent bindings shown; participant bindings omitted)
      court_name_field:      { field: 'court_name' },
      case_number_field:     { field: 'case_number' },
      charges_field:         { field: 'charges' },
      bond_amount_field:     { field: 'bond_amount_numeric' },
      poa_number_field:      { field: 'poa_number' },
    },
  },
  {
    agency_id: 'a_affordable',
    companyNumber: 1008,
    document_slug: 'a_affordable_ccp',
    template_version: 1,
    display_name: 'Credit Card Promise / Unpaid Premium',
    summary:
      'Indemnitor commitment to pay the unpaid premium and ' +
      'authorization to charge the credit card on file if payment ' +
      "terms aren't met.",
    signer_role: 'indemnitor',
    surety_affiliation: 'allegheny',
    status: 'active',
    bindings: {
      bond_amount_field:    { field: 'bond_amount_numeric' },
      premium_amount_field: { field: 'premium_amount' },
      case_number_field:    { field: 'case_number' },
    },
  },
];

// ─── Agencies ───────────────────────────────────────────────────
// Just A-Affordable for now. PNWBB (1002) has no agency record
// yet — when a 1002 user opens the wizard, getAgency returns null
// and the wizard renders an empty-library state in Step 2.
const AGENCIES = {
  1008: {
    agency_id: 'a_affordable',
    companyNumber: 1008,
    display_name: 'A-Affordable Bail Bonds',
    surety_affiliation: 'allegheny',
    default_jurisdiction: 'clark',
    allowed_jurisdictions: ['clark'],
  },
};

// ─── API surface ────────────────────────────────────────────────
// Drop-in replaceable. Replace each function body with a fetch()
// call to the real API and the wizard works unchanged.
export const wizardData = {
  /** Returns the agency record for a given companyNumber, or null. */
  getAgency(companyNumber) {
    return AGENCIES[companyNumber] || null;
  },

  /** Returns active documents owned by the agency. */
  getDocumentsForAgency(agencyId) {
    return DOCUMENTS.filter(
      d => d.agency_id === agencyId && d.status === 'active'
    );
  },

  /** Returns a field definition by id, or null. */
  getField(fieldId) {
    return FIELDS[fieldId] || null;
  },

  /** Returns a section definition by id, or null. */
  getSection(sectionId) {
    return SECTIONS[sectionId] || null;
  },

  /**
   * Returns the union of agent-audience required fields across
   * the selected documents, grouped by section and sorted.
   *
   * Returns: [{ section, sectionLabel, sectionOrder, fields: [...] }, ...]
   */
  deriveAgentFields(selectedDocSlugs) {
    // Step 1: gather field_ids referenced by all bindings on
    // the selected documents.
    const referencedFieldIds = new Set();
    for (const slug of selectedDocSlugs) {
      const doc = DOCUMENTS.find(d => d.document_slug === slug);
      if (!doc) continue;
      for (const binding of Object.values(doc.bindings || {})) {
        if (binding.ignored) continue;
        if (binding.field) referencedFieldIds.add(binding.field);
      }
    }

    // Step 2: filter to agent-audience required fields.
    const fields = [];
    for (const fid of referencedFieldIds) {
      const f = FIELDS[fid];
      if (!f) continue;
      if (f.audience !== 'agent') continue;
      if (f.default_requirement !== 'required') continue;
      fields.push(f);
    }

    // Step 3: group by section.
    const bySection = {};
    for (const f of fields) {
      if (!bySection[f.section]) {
        const sec = SECTIONS[f.section] || { order: 99, label: f.section };
        bySection[f.section] = {
          section: f.section,
          sectionLabel: sec.label,
          sectionOrder: sec.order,
          fields: [],
        };
      }
      bySection[f.section].fields.push(f);
    }

    // Step 4: sort sections by order, fields within section by
    // section_order.
    const groups = Object.values(bySection);
    groups.sort((a, b) => a.sectionOrder - b.sectionOrder);
    for (const g of groups) {
      g.fields.sort((a, b) => (a.section_order || 0) - (b.section_order || 0));
    }
    return groups;
  },
};
